import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';

import {ApiResource} from 'common/util/model-resource/api-resource';
import {
  annotationsClient,
  createAnnotation,
  CreateAnnotationParams,
  deleteAnnotation,
  updateAnnotation,
} from 'common/report/queries/app-api/annotations-client';
import {Annotation, AnnotationGroup} from 'common/report/model-resources/annotations/types';
import {TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {formatAnnotationDateForGroup, parseAnnotationDate} from 'common/report/model-resources/annotations/utils';
import {Timezone} from 'common/constants';
import {SortOrderType} from 'common/types/reports/bookmark';

const CREATE_KEY = `create`;

// TODO(at) /annotations endpoint is slow (takes ~9s for ~650 annotations on project 4). visually, annotations render
// slowly only on first load, as cached values are used till /annotations endpoint return. nonetheless, should see if
// /annotations endpoint can be sped up. otherwise, should consider switching to the old approach of including
// from_date/to_date from the chart in the /annotations query (and requerying whenever date range changes) to speed
// up the request.
export class AnnotationsResource extends ApiResource<Annotation> {
  apiClient = annotationsClient;

  private async upsertWrapper(loadingKey: string, requestFn: () => Promise<Annotation>) {
    // @ts-ignore
    this.fetchesInProgress.set(loadingKey, undefined);
    this.notifySubscribers();
    try {
      const annotation = await requestFn();

      this.update({[annotation.id]: annotation}, {partialUpdate: true});

      return annotation;
    } finally {
      this.fetchesInProgress.delete(loadingKey);
      this.notifySubscribers();
    }
  }

  getSortedAnnotations({
    fromDate,
    toDate,
    tagFilterIds,
    showUntagged,
    annotationsSort,
  }: {
    fromDate?: string;
    toDate?: string;
    tagFilterIds?: number[];
    showUntagged?: boolean;
    annotationsSort?: SortOrderType;
  } = {}): Annotation[] {
    const tagFilterIdSet = new Set(tagFilterIds ?? []);
    const annotations = this.getAllValues().filter((annotation) => {
      if (tagFilterIdSet.size || showUntagged) {
        const isMatchingAnnotation =
          (annotation.tags ?? []).some((tag) => tagFilterIdSet.has(tag.id)) ||
          ((annotation.tags ?? []).length === 0 && showUntagged);
        if (!isMatchingAnnotation) {
          return false;
        }
      }
      const annotationDate = parseAnnotationDate(annotation.date).format(`YYYY-MM-DD`);
      return (fromDate ? annotationDate >= fromDate : true) && (toDate ? annotationDate <= toDate : true);
    });
    return orderBy(annotations, (annotation) => annotation.date, annotationsSort || `asc`);
  }

  getAnnotationGroups({
    fromDate,
    toDate,
    timezone,
    timeUnit,
    tagFilterIds,
    showUntagged,
    annotationsSort,
  }: {
    fromDate?: string;
    toDate?: string;
    timezone: Timezone;
    timeUnit?: TimeUnit;
    showUntagged?: boolean;
    tagFilterIds?: number[];
    annotationsSort?: SortOrderType;
  }): AnnotationGroup[] {
    const annotations = this.getSortedAnnotations({fromDate, toDate, tagFilterIds, showUntagged, annotationsSort});
    const groupedAnnotations = groupBy(annotations, (annotation) => {
      return formatAnnotationDateForGroup(annotation.date, {timezone, timeUnit});
    });

    // NOTE: groupedAnnotation is an object with the annotations grouped by date. the expectation is that they will
    // be in sorted order when traversed. This should be the case given lodash's groupBy which takes a sorted list
    // of annotations as input.
    return Object.entries(groupedAnnotations).map(([formattedDate, annotationInGroup]) => {
      return {formattedDate, annotations: annotationInGroup};
    });
  }

  updateAnnotation(id: number, update: Pick<Annotation, `description` | `tags`>) {
    return this.upsertWrapper(id.toString(), () => updateAnnotation(id, update));
  }

  createAnnotation(params: CreateAnnotationParams): Promise<Annotation> {
    return this.upsertWrapper(CREATE_KEY, () => createAnnotation(params));
  }

  async deleteAnnotation(id: number): Promise<void> {
    await deleteAnnotation(id);
    super.delete(id.toString());
  }

  setHovered(id: number, isHovered: boolean) {
    this.getAllValues().forEach((value) => (value.hovered = false));
    const annotation = this.getOne(id).value;
    if (annotation) {
      annotation.hovered = isHovered;
    }
    this.notifySubscribers();
  }
}
